// Per-product ATC button colors (matches collection page)
const UPSELL_PRODUCT_COLORS = {
  9685846982949: { bg: '#ff6b0f', border: '#bc4800' }, // Mango Passion
  9683255984421: { bg: '#e7004b', border: '#ab093c' }, // Banana Strawberry
  9817136890149: { bg: '#008fbe', border: '#006580' }, // Simply Vanilla
  9817141051685: { bg: '#763cbc', border: '#510d77' }, // Double Chocolate
};
const UPSELL_DEFAULT_COLOR = { bg: '#00a98e', border: '#055d5d' }; // Variety Pack / fallback

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.cartProductIds = new Set();
    this.upsellContainer = null;
    this.addToCartLabel = this.dataset.addToCartLabel || 'Add [[title]] to cart';
  }

  connectedCallback() {
    this.upsellContainer = document.getElementById('CartDrawer-Upsell');
    if (!this.upsellContainer) return;

    this.loadProducts();
    this.subscribeToCartUpdates();
    this.initArrows();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initArrows() {
    const prevBtn = this.upsellContainer.querySelector('.cart-drawer__upsell-prev');
    const nextBtn = this.upsellContainer.querySelector('.cart-drawer__upsell-next');
    if (prevBtn) prevBtn.addEventListener('click', () => this.scroll('prev'));
    if (nextBtn) nextBtn.addEventListener('click', () => this.scroll('next'));
  }

  scroll(direction) {
    const list = this.upsellContainer.querySelector('.cart-drawer__upsell-list');
    if (!list) return;
    const card = list.querySelector('.cart-drawer__upsell-card');
    if (!card) return;
    const scrollAmount = card.offsetWidth + 10; // card width + gap
    list.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }

  subscribeToCartUpdates() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.updateCartProductIds();
        this.renderProducts();
      });
    }
  }

  updateCartProductIds() {
    this.cartProductIds.clear();
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach((item) => {
      const productLink = item.querySelector('.cart-item__name, a[href*="/products/"]');
      if (productLink && productLink.href) {
        const handle = this.extractHandle(productLink.href);
        if (handle) this.cartProductIds.add(handle);
      }
    });
  }

  extractHandle(url) {
    const match = url.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  async loadProducts() {
    try {
      const response = await fetch('/collections/all/products.json?limit=10');
      if (response.ok) {
        const data = await response.json();
        this.products = data.products.map((product) => {
          const v = product.variants && product.variants[0];
          return {
            id: product.id,
            handle: product.handle,
            url: `/products/${product.handle}`,
            title: product.title,
            productType: product.product_type || '',
            image: product.images && product.images[0] ? product.images[0].src : '',
            price: v ? this.formatMoney(v.price) : '',
            variantId: v ? v.id : null,
            available: v ? v.available : false,
          };
        });
        this.updateCartProductIds();
        this.renderProducts();
      }
    } catch (e) {
      console.error('Upsell: Could not load products', e);
    }
  }

  formatMoney(price) {
    return '$' + Number(price).toFixed(2);
  }

  getFilteredProducts() {
    return this.products.filter((p) => !this.cartProductIds.has(p.handle) && p.available);
  }

  renderProducts() {
    const list = this.upsellContainer?.querySelector('.cart-drawer__upsell-list');
    if (!list) return;

    const filtered = this.getFilteredProducts();

    if (filtered.length === 0) {
      this.upsellContainer.style.display = 'none';
      return;
    }

    this.upsellContainer.style.display = 'block';
    list.innerHTML = '';

    filtered.forEach((product) => {
      const card = document.createElement('li');
      card.className = 'cart-drawer__upsell-card';
      card.innerHTML = this.cardHTML(product);
      list.appendChild(card);
    });

    // Hide arrows if 3 or fewer products (they all fit in view)
    const arrows = this.upsellContainer.querySelector('.cart-drawer__upsell-arrows');
    if (arrows) {
      arrows.style.display = filtered.length > 3 ? 'flex' : 'none';
    }
  }

  cardHTML(product) {
    const clr = UPSELL_PRODUCT_COLORS[product.id] || UPSELL_DEFAULT_COLOR;
    const btnStyle = `background-color:${clr.bg};border:1px solid ${clr.border};box-shadow:2px 2px 0 1px ${clr.border}`;
    const cartIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>`;

    const atcLabel = this.addToCartLabel.replace('[[title]]', product.title);
    const atcButton = product.variantId && product.available
      ? `<button type="button" class="cart-drawer__upsell-atc" style="${btnStyle}" data-variant-id="${product.variantId}" data-btn-bg="${clr.bg}" data-btn-border="${clr.border}" aria-label="${atcLabel}">${cartIcon}</button>`
      : '';

    return `
      <a href="${product.url}" class="cart-drawer__upsell-card-image" aria-hidden="true" tabindex="-1">
        ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy" width="200" height="200">` : ''}
      </a>
      <div class="cart-drawer__upsell-card-info">
        ${product.productType ? `<span class="cart-drawer__upsell-card-type">${product.productType}</span>` : ''}
        <a href="${product.url}" class="cart-drawer__upsell-card-title">${product.title}</a>
        <div class="cart-drawer__upsell-card-row">
          <span class="cart-drawer__upsell-card-price">${product.price}</span>
          ${atcButton}
        </div>
      </div>
    `;
  }
}

// Delegate add-to-cart clicks
document.addEventListener('click', function (e) {
  const btn = e.target.closest('.cart-drawer__upsell-atc[data-variant-id]');
  if (!btn) return;

  e.preventDefault();
  const variantId = btn.dataset.variantId;
  const originalHTML = btn.innerHTML;
  const checkIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  btn.classList.add('loading');

  const body = JSON.stringify({
    items: [{ id: parseInt(variantId, 10), quantity: 1 }],
  });

  fetch(window.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body,
  })
    .then((r) => r.json())
    .then(() => {
      btn.innerHTML = checkIcon;
      // Publish cart update so drawer & icon refresh
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell' });
      }
      setTimeout(() => {
        btn.classList.remove('loading');
        btn.innerHTML = originalHTML;
      }, 1200);
    })
    .catch(() => {
      btn.innerHTML = originalHTML;
      setTimeout(() => {
        btn.classList.remove('loading');
      }, 1500);
    });
});

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
